import { Locale, REDIS_KEY_SEPARATOR, RedisConfig } from '@headless-workspace/config';
import Redis from 'ioredis';
import { IsomorphicLogger } from '../logger';
import { initRedis } from './InitRedis';

export type CacheOptions = {
    ttl?: number;
    staleWhileRevalidate?: number;
    tags?: string[];
};

type CachedData<T> = {
    data: T;
    cachedAt: number;
    staleAfter: number;
    tags: string[];
};

const logger = new IsomorphicLogger();

export class CacheService {
    private defaultTTL = 1800; // 30 minutes
    private defaultSWR = 900; // 15 minutes

    private async getRedisInstance(): Promise<Redis | null> {
        return initRedis();
    }

    private generateKey(locale: Locale, prefix: string, params: Record<string, any>): string {
        const sortedParams = Object.keys(params)
            .sort((a, b) => a.localeCompare(b))
            .map((key) => `${key}:${JSON.stringify(params[key])}`)
            .join('|');

        return [RedisConfig.GlobalPrefix, locale, prefix, sortedParams].join(REDIS_KEY_SEPARATOR);
    }

    async get<T>(
        locale: Locale,
        prefix: string,
        params: Record<string, any>,
    ): Promise<{
        data: T | null;
        isStale: boolean;
    }> {
        try {
            const key = this.generateKey(locale, prefix, params);

            const redis = await this.getRedisInstance();
            const cached = await redis?.get(key);

            if (!cached) {
                return { data: null, isStale: false };
            }

            const cachedData: CachedData<T> = JSON.parse(cached);
            const now = Date.now();
            const isStale = now > cachedData.staleAfter;

            return { data: cachedData.data, isStale };
        } catch (error) {
            logger.error('Cache get error:', error);
            return { data: null, isStale: false };
        }
    }

    async set<T>(
        locale: Locale,
        prefix: string,
        params: Record<string, any>,
        data: T,
        options: CacheOptions = {},
    ): Promise<void> {
        try {
            const redis = await this.getRedisInstance();

            // Silently skip caching when Redis is not available
            if (!redis) {
                return;
            }

            const key = this.generateKey(locale, prefix, params);
            const ttl = options.ttl || this.defaultTTL;
            const swr = options.staleWhileRevalidate || this.defaultSWR;

            const cachedData: CachedData<T> = {
                data,
                cachedAt: Date.now(),
                staleAfter: Date.now() + swr * 1000,
                tags: options.tags || [],
            };

            await redis.setex(key, ttl, JSON.stringify(cachedData));

            // Store tag mappings for invalidation
            if (options.tags?.length) {
                const pipeline = redis.pipeline();

                options.tags.forEach((tag) => {
                    const cacheTagKey = [RedisConfig.GlobalPrefix, locale, 'tags', tag].join(REDIS_KEY_SEPARATOR);
                    pipeline.sadd(cacheTagKey, key);
                    pipeline.expire(cacheTagKey, ttl);
                });
                await pipeline.exec();
            }
        } catch (error) {
            logger.error('Cache set error:', error);
        }
    }

    async del(locale: Locale, prefix: string, params: Record<string, any>): Promise<void> {
        try {
            const redis = await this.getRedisInstance();
            // Silently skip caching when Redis is not available
            if (!redis) {
                return;
            }

            const key = this.generateKey(locale, prefix, params);
            await redis.del(key);
        } catch (error) {
            logger.error('Cache delete error:', error);
        }
    }

    async invalidateByTags(locale: Locale, tags: string[]): Promise<void> {
        try {
            const redis = await this.getRedisInstance();

            // Silently skip caching when Redis is not available
            if (!redis) {
                return;
            }

            const pipeline = redis.pipeline();

            for (const tag of tags) {
                const tagKey = [RedisConfig.GlobalPrefix, locale, 'tags', tag].join(REDIS_KEY_SEPARATOR);
                const keys = (await redis?.smembers(tagKey)) ?? [];

                if (keys.length > 0) {
                    pipeline.del(...keys);
                }
                pipeline.del(tagKey);
            }

            await pipeline.exec();
        } catch (error) {
            logger.error('Cache invalidation error:', error);
        }
    }
}
